// Entry point for /embed/awning.html — standalone visualizer for iframe
// embedding (breslow.com/retractable-awnings/, sister-company sites). Strips
// the wizard, concierge flow, and lead capture from the wizard build. Only
// imports what the visualizer + compass + fabric picker need.
//
// Initial state can be overridden via URL query params:
//   ?width=small|medium|large     → awning tier
//   ?tier=essential|signature|heritage
//   ?fabric=<sku>                 → e.g. 4608-0000
//   ?state=NJ|NY|CT|PA            → location for sun path
//   ?facing=<degrees>             → 0=N, 90=E, 180=S, 270=W
// Unrecognized params are ignored silently.

import { AwningApp } from './three/AwningApp.js';
import { bindFabricPicker } from './wizard/fabricPicker.js';
import { bindCompass } from './wizard/compass.js';
import fabricsData from './data/fabrics-sunbrella-shade.json';

const $ = (sel) => document.querySelector(sel);

// Minimal state shim — fabricPicker reads `state.fabric` and calls
// `state.set('fabric', record)`. WizardState (which brings the whole
// concierge flow with it) is intentionally NOT imported here.
function createMiniState(initialFabric) {
  return {
    fabric: initialFabric,
    set(key, value) { this[key] = value; },
  };
}

const WIDTH_KEY_MAP = { small: 'S', medium: 'M', large: 'L' };
const VALID_TIERS   = new Set(['essential', 'signature', 'heritage']);
const VALID_STATES  = new Set(['NJ', 'NY', 'CT', 'PA']);

function parseQueryParams() {
  const params = new URLSearchParams(window.location.search);
  const out = {};

  const width = (params.get('width') || '').toLowerCase();
  if (WIDTH_KEY_MAP[width]) out.widthKey = WIDTH_KEY_MAP[width];

  const tier = (params.get('tier') || '').toLowerCase();
  if (VALID_TIERS.has(tier)) out.tier = tier;

  const stateAbbr = (params.get('state') || '').toUpperCase();
  if (VALID_STATES.has(stateAbbr)) out.stateChosen = stateAbbr;

  const facingRaw = params.get('facing');
  if (facingRaw !== null) {
    const deg = parseFloat(facingRaw);
    if (!Number.isNaN(deg)) out.facingDeg = ((deg % 360) + 360) % 360;
  }

  const sku = params.get('fabric');
  if (sku) {
    const match = fabricsData.fabrics.find(f => f.sku === sku);
    if (match) out.fabric = match;
  }

  return out;
}

function init() {
  const canvas = $('#awning-canvas');
  if (!canvas) return;

  const defaultFabric =
    fabricsData.fabrics.find(f => f.sku === '4674-0000') || fabricsData.fabrics[0];
  const defaults = {
    widthKey: 'M',
    tier: 'signature',
    stateChosen: 'NJ',
    facingDeg: 270,
    fabric: defaultFabric,
  };
  const overrides = parseQueryParams();
  const initial = { ...defaults, ...overrides };

  console.log('[embed] initial state:', {
    widthKey: initial.widthKey,
    tier: initial.tier,
    stateChosen: initial.stateChosen,
    facingDeg: initial.facingDeg,
    fabric: `${initial.fabric.name} (${initial.fabric.sku})`,
  });
  if (Object.keys(overrides).length) {
    console.log('[embed] applied query params:', Object.keys(overrides));
  }

  // Mount the 3D app immediately.
  const app = new AwningApp(canvas);
  app.setTier(initial.tier);
  app.setWidthKey(initial.widthKey);
  app.setStateChosen(initial.stateChosen);
  app.setFacingDeg(initial.facingDeg);
  app.setFabric(initial.fabric);
  app.mount();

  // Compass + fabric picker share a minimal state shim.
  const miniState = createMiniState(initial.fabric);
  bindFabricPicker(miniState, (record) => app.setFabric(record));
  const compass = bindCompass(miniState, app);
  // Sync the compass rectangle's local facingDeg with our initial override
  // (the compass otherwise defaults to 270 internally).
  if (compass && compass.setFacingDeg && initial.facingDeg !== 270) {
    compass.setFacingDeg(initial.facingDeg);
  }

  wireSliders(app);
}

function wireSliders(app) {
  const timeSlider = $('#time-slider');
  const timeReadout = $('#time-readout');
  if (timeSlider) {
    const setTime = (v) => {
      app.setTimeMinutes(parseInt(v, 10));
      if (timeReadout) timeReadout.textContent = app.getSunController().getTimeDisplay();
    };
    setTime(timeSlider.value);
    timeSlider.addEventListener('input', (e) => setTime(e.target.value));
  }

  const datePicker = $('#date-picker');
  const dateReadout = $('#date-readout');
  if (datePicker) {
    datePicker.value = app.getSunController().getDateInputValue();
    const update = () => {
      const d = new Date(datePicker.value + 'T12:00:00');
      if (dateReadout && !Number.isNaN(d.getTime())) {
        dateReadout.textContent = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    };
    update();
    datePicker.addEventListener('change', () => {
      app.setDateFromString(datePicker.value);
      update();
    });
  }

  const extSlider = $('#ext-slider');
  const extReadout = $('#ext-readout');
  if (extSlider) {
    const set = (v) => {
      const pct = parseInt(v, 10);
      app.setExtension(pct / 100);
      if (extReadout) extReadout.textContent = `${pct}%`;
    };
    set(extSlider.value);
    extSlider.addEventListener('input', (e) => set(e.target.value));
  }

  const pitchSlider = $('#pitch-slider');
  const pitchReadout = $('#pitch-readout');
  if (pitchSlider) {
    const set = (v) => {
      const deg = parseInt(v, 10);
      app.setPitchDeg(deg);
      if (pitchReadout) pitchReadout.textContent = `${deg}°`;
    };
    set(pitchSlider.value);
    pitchSlider.addEventListener('input', (e) => set(e.target.value));
  }

  const dropSlider = $('#drop-slider');
  const dropReadout = $('#drop-readout');
  if (dropSlider) {
    const set = (v) => {
      const pct = parseInt(v, 10);
      app.setDropScreenExtension(pct / 100);
      if (dropReadout) dropReadout.textContent = `${pct}%`;
    };
    set(dropSlider.value);
    dropSlider.addEventListener('input', (e) => set(e.target.value));
  }

  const shadeReadout = $('#shade-readout');
  if (shadeReadout) {
    app.onShadeScore((score) => {
      if (shadeReadout.firstChild) {
        shadeReadout.firstChild.textContent = `${Math.round(score * 100)}% `;
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
